// 案 A: 役員速報 + 全科比率テーブル (フィルタ drawer 付き) — 2026-05-26 PC 入院実績 タブと spec を共通化
// 構成 (上から):
//   1. 主要 18 cards (大、 sparkline + 予算/前年 badges): BUDGET_TAB_SPECS の !tableOnly (2 列 × 9 行)
//   2. 全科 予算比/前年比 表 (入院収入): 病院全体 + 全診療科、 行タップで focus 連動
// - 大 cards 形式に統一 (旧 big4 + mini14 の区別撤廃 — 2026-05-26 user feedback)
// - 右上 ⚙ ボタンで filter drawer (粒度/医療区分/予定緊急/対象月/科 focus)
// - 表の行タップで focus → 上の 18 cards が該当科の数値に差替 (病院全体 行は focus 解除)
// - dept_metrics_all 1 query で全 cards 描画 (PC と data 整合)

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { useMeta } from "../lib/mobileApi";
import { MobileShell } from "../components/MobileShell";
import { StatPill, sentimentFromRatio, sentimentFromDelta, SENTIMENT_BG, SENTIMENT_RING } from "../components/StatBadge";
import { Sparkline } from "../components/Sparkline";
import { FilterDrawer, type MobileFilterState } from "../components/FilterDrawer";
import { fmtByUnitMain, fmtRatio } from "../../lib/format";
import { resolveAllSpecs, type ResolvedMetric } from "../../components/prototypes/budgetSpec";
import type { DeptMetricsAllResponse, PortfolioResponse, Iryo, Yotei, Granularity } from "@shared/api";

// 月末未確定で 0 になる指標 (PC と同じ、 派生も含む)
const SKIP_ZERO_KEYS = new Set([
  "GP", "GP単価", "GP単価_在院",
  "出来高換算薬剤費", "出来高換算材料費", "出来高換算薬剤材料費",
]);

export function PrototypeA() {
  const { data: meta } = useMeta();
  const [filterOpen, setFilterOpen] = useState(false);

  // filter state. meta 読み込み前は仮値、 読み込み後に asOfMonth を dateMax に差替
  const [filter, setFilter] = useState<MobileFilterState>({
    granularity: "小",
    iryo: "合計",
    yotei: "合計",
    asOfMonth: "2026-04-01",
    focusedDept: null,
  });

  useEffect(() => {
    if (meta && filter.asOfMonth !== meta.dateMax && filter.asOfMonth === "2026-04-01") {
      setFilter((f) => ({ ...f, asOfMonth: meta.dateMax }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta?.dateMax]);

  // dept_metrics_all 1 query で全 cards の data を取得 (PC と同じ endpoint・派生計算)
  const periodFrom = monthShift(filter.asOfMonth, -11);
  const { data: deptData, isLoading: deptLoading } = useQuery<DeptMetricsAllResponse>({
    queryKey: ["mobile-a-dept", filter.granularity, filter.iryo, filter.yotei, filter.asOfMonth, periodFrom, filter.focusedDept],
    queryFn: () => api.deptMetricsAll({
      granularity: filter.granularity, iryo: filter.iryo, yotei: filter.yotei,
      asOfMonth: filter.asOfMonth, periodFrom,
      focusedDept: filter.focusedDept ?? undefined,
    }),
    placeholderData: (prev) => prev,
    enabled: !!meta,
  });

  const resolved = useMemo(() => deptData ? resolveAllSpecs(deptData) : [], [deptData]);
  // tableOnly を除外 (= 18 指標、 PC 6×3 grid と同じセット、 spec 順 = PC Row1→Row2→Row3 順)
  const mainCards = useMemo(() => resolved.filter((r) => !r.spec.tableOnly), [resolved]);

  if (!meta) return <div className="p-6 text-sm text-slate-400">読み込み中…</div>;

  const ymLabel = `${filter.asOfMonth.slice(0, 4)}年${filter.asOfMonth.slice(5, 7)}月`;
  const filterSummary = [
    filter.granularity + "分類",
    filter.iryo,
    filter.yotei,
  ].join(" / ");

  return (
    <>
      <MobileShell
        title={filter.focusedDept ? `🎯 ${filter.focusedDept}` : "病院全体 速報"}
        prototypeId="A"
        prototypeName="役員速報"
        asOfMonth={filter.asOfMonth}
        onPrevMonth={() => setFilter((f) => ({ ...f, asOfMonth: monthShift(f.asOfMonth, -1) }))}
        onNextMonth={() => setFilter((f) => ({ ...f, asOfMonth: monthShift(f.asOfMonth, +1) }))}
        canPrevMonth={monthShift(filter.asOfMonth, -1).slice(0, 7) >= meta.dateMin.slice(0, 7)}
        canNextMonth={monthShift(filter.asOfMonth, +1).slice(0, 7) <= meta.dateMax.slice(0, 7)}
        headerRight={
          <button
            type="button"
            onClick={() => setFilterOpen(true)}
            className="text-slate-500 hover:text-slate-800 active:bg-slate-100 rounded p-1.5 text-base leading-none"
            title="フィルタを開く"
            aria-label="フィルタ"
          >⚙</button>
        }
      >
        <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
          <span className="bg-slate-100 px-1.5 py-0.5 rounded">{filterSummary}</span>
          {filter.focusedDept && (
            <button
              onClick={() => setFilter((f) => ({ ...f, focusedDept: null }))}
              className="bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded border border-amber-200 active:bg-amber-200"
            >× フォーカス解除</button>
          )}
          {deptLoading && <span className="text-slate-400">loading…</span>}
        </div>

        <section>
          <div className="text-xs text-slate-500 mb-1">{ymLabel} 主要 18 指標 (PC と共通 spec)</div>
          <div className="grid grid-cols-2 gap-2">
            {mainCards.map((r) => (
              <BigKpiCard key={r.spec.key} r={r} />
            ))}
          </div>
        </section>

        <RatioTable
          asOfMonth={filter.asOfMonth}
          periodFrom={periodFrom}
          granularity={filter.granularity}
          iryo={filter.iryo}
          yotei={filter.yotei}
          anchorResolved={resolved.find((r) => r.spec.key === "入院収入") ?? null}
          onSelectDept={(d) => setFilter((f) => ({ ...f, focusedDept: d }))}
          focusedDept={filter.focusedDept}
        />

        <p className="text-[10px] text-slate-400 text-center px-4 leading-relaxed">
          詳細分析は PC 版へ。 このページは「今月確認すべきこと」を 30 秒で把握する用。
        </p>
      </MobileShell>

      <FilterDrawer
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        state={filter}
        setState={setFilter}
        meta={meta}
      />
    </>
  );
}

// 大カード (2 列): FEATURED_KEYS の 4 指標。 ResolvedMetric (派生も解決済み) を受け取る。
function BigKpiCard({ r }: { r: ResolvedMetric }) {
  const { spec } = r;
  const skipZero = SKIP_ZERO_KEYS.has(spec.key);
  const adj = (v: number | null): number | null => v == null ? null : (skipZero && v === 0 ? null : v);
  const cur = adj(r.current);
  const bud = adj(r.budget);
  const py  = adj(r.prevYear);

  const main = fmtByUnitMain(cur, spec.unit);
  const ratio = (cur != null && bud != null && bud !== 0) ? cur / bud : null;
  const ratioSent = sentimentFromRatio(ratio, spec.direction);
  const yoyDelta = (cur != null && py != null && py !== 0) ? (cur - py) / Math.abs(py) : null;
  const yoySent = sentimentFromDelta(yoyDelta, spec.direction);

  // sparkline: 0/null は除外 (SKIP_ZERO 該当時の月末未確定を非表示に)
  const sparkValues = r.trend.map((p) => adj(p.actual) ?? 0);
  const sparkValid = r.trend.filter((p) => adj(p.actual) != null).length;

  return (
    <div className={`p-2.5 rounded-lg ring-1 ${SENTIMENT_RING[ratioSent]} ${SENTIMENT_BG[ratioSent]}`}>
      <div className="text-[11px] text-slate-600 font-medium leading-tight">{spec.label}</div>
      <div className="text-[9px] text-slate-400 leading-tight">{spec.unit}</div>
      <div className="mt-1 flex items-baseline gap-0.5">
        <span className="text-xl font-bold text-slate-900 tabular-nums">{main.value}</span>
      </div>
      {sparkValid > 1 && (
        <div className="mt-0.5 -mx-1">
          <Sparkline values={sparkValues} width={140} height={20} showLast />
        </div>
      )}
      <div className="mt-1 flex flex-wrap gap-1">
        <StatPill label="予算" value={ratio != null ? fmtRatio(ratio) : "—"} sentiment={ratioSent} size="xs" />
        <StatPill label="前年" value={yoyDelta != null ? `${yoyDelta >= 0 ? "+" : ""}${(yoyDelta * 100).toFixed(1)}%` : "—"} sentiment={yoySent} size="xs" />
      </div>
    </div>
  );
}

// 全科 予算比/前年比 table (入院収入) — 病院全体 + 全診療科。 行タップで focus 連動。
// 並び順: 病院全体 (先頭) → 予算比 昇順 (悪い順、 異常検知用)、 null は最後尾。
function RatioTable({
  asOfMonth, periodFrom, granularity, iryo, yotei,
  anchorResolved, onSelectDept, focusedDept,
}: {
  asOfMonth: string; periodFrom: string;
  granularity: Granularity; iryo: Iryo; yotei: Yotei;
  anchorResolved: ResolvedMetric | null;
  onSelectDept: (d: string | null) => void;
  focusedDept: string | null;
}) {
  // 各科の予算比/前年比 を portfolio endpoint (入院収入) で取得
  const { data, isLoading } = useQuery<PortfolioResponse>({
    queryKey: ["mobile-a-ratio", granularity, iryo, yotei, asOfMonth, periodFrom],
    queryFn: () => api.portfolio({
      granularity, iryo, yotei, asOfMonth, periodFrom, metric: "入院収入",
    }),
    placeholderData: (prev) => prev,
  });

  type Row = { dept: string; budgetRatio: number | null; yoyRatio: number | null; isTotal: boolean };

  const rows = useMemo<Row[]>(() => {
    if (!data) return [];
    // 病院全体 (anchor) — dept_metrics_all の 入院収入 から計算
    const a = anchorResolved;
    const anchor: Row | null = a ? {
      dept: "病院全体",
      budgetRatio: (a.current != null && a.budget   != null && a.budget   !== 0) ? a.current / a.budget   : null,
      yoyRatio:    (a.current != null && a.prevYear != null && a.prevYear !== 0) ? a.current / a.prevYear : null,
      isTotal: true,
    } : null;
    // 各科 — portfolio response から (worst-first 並び)
    const deptRows: Row[] = data.rows.map((r) => ({
      dept: r["診療科"],
      budgetRatio: r.budget_ratio,
      yoyRatio:    r.yoy_ratio,
      isTotal: false,
    }));
    deptRows.sort((x, y) => (x.budgetRatio ?? Infinity) - (y.budgetRatio ?? Infinity));
    return [...(anchor ? [anchor] : []), ...deptRows];
  }, [data, anchorResolved]);

  return (
    <section>
      <div className="flex items-baseline gap-2 mb-1">
        <h2 className="text-xs font-semibold text-slate-700">📊 全科 比率 (入院収入)</h2>
        <span className="text-[10px] text-slate-400">行タップで上の cards が連動</span>
        {isLoading && <span className="text-[9px] text-slate-400">loading…</span>}
      </div>
      <div className="text-[10px] text-slate-400 mb-1 flex flex-wrap items-center gap-x-1.5">
        <span className="inline-block w-2.5 h-2.5 bg-emerald-300 border border-slate-300"></span>+10%↑
        <span className="inline-block w-2.5 h-2.5 bg-emerald-100 border border-slate-300"></span>+5%↑
        <span className="inline-block w-2.5 h-2.5 bg-white border border-slate-300"></span>±5%
        <span className="inline-block w-2.5 h-2.5 bg-red-100 border border-slate-300"></span>-5%↓
        <span className="inline-block w-2.5 h-2.5 bg-red-300 border border-slate-300"></span>-10%↓
        <span>(入院収入は 高い = 良い)</span>
      </div>
      <div className="text-xs">
        <table className="w-full border-collapse">
          <thead className="bg-slate-100 text-[10px] text-slate-600">
            <tr>
              <th className="text-left  px-2 py-1 border-b border-slate-200">診療科</th>
              <th className="text-right px-2 py-1 border-b border-slate-200 w-16">予算比</th>
              <th className="text-right px-2 py-1 border-b border-slate-200 w-16">前年比</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const isFocused = r.isTotal ? focusedDept === null : focusedDept === r.dept;
              const onClick = () => {
                if (r.isTotal) onSelectDept(null);
                else onSelectDept(isFocused ? null : r.dept);
              };
              const budBg = ratioCellBg(r.budgetRatio);
              const yoyBg = ratioCellBg(r.yoyRatio);
              return (
                <tr key={r.dept}
                    onClick={onClick}
                    className={`cursor-pointer active:bg-amber-100 ${isFocused
                      ? "border-y-2 border-amber-500 bg-amber-50/40"
                      : "border-b border-slate-100"}`}>
                  <td className={`text-left px-2 py-1.5 font-medium ${r.isTotal ? "bg-slate-50" : ""}`}>
                    {r.isTotal ? <span className="mr-1">🏥</span> : isFocused ? <span className="mr-1">🎯</span> : null}
                    {r.dept}
                  </td>
                  <td className={`text-right px-2 py-1.5 tabular-nums font-semibold ${budBg}`}>
                    {r.budgetRatio != null ? fmtRatio(r.budgetRatio) : "—"}
                  </td>
                  <td className={`text-right px-2 py-1.5 tabular-nums font-semibold ${yoyBg}`}>
                    {r.yoyRatio != null ? fmtRatio(r.yoyRatio) : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// 比率 (1.0=100%) を 5 階調 cellBg に。 入院収入 は higher_is_better 固定 (mobile A は 入院収入 のみ)
function ratioCellBg(ratio: number | null): string {
  if (ratio == null) return "";
  const delta = ratio - 1;
  if (delta >= 0.10) return "bg-emerald-300";
  if (delta >= 0.05) return "bg-emerald-100";
  if (delta >= -0.05) return "";
  if (delta >= -0.10) return "bg-red-100";
  return "bg-red-300";
}

function monthShift(d: string, delta: number): string {
  const [y, m, day] = d.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1 + delta, day));
  return [dt.getUTCFullYear(), String(dt.getUTCMonth() + 1).padStart(2, "0"), String(dt.getUTCDate()).padStart(2, "0")].join("-");
}
